(() => {
  'use strict';
  const CAROUSEL = '[data-bl-v9-focus-carousel]';
  const SLIDE = '[data-bl-v9-focus-slide]';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const initialize = (carousel) => {
    if (!carousel || carousel.dataset.blV9FocusReady === 'true') return;
    const viewport = carousel.querySelector('.bl-v9-feature-carousel__viewport');
    const track = carousel.querySelector('[data-bl-v9-focus-track]');
    const slides = Array.from(carousel.querySelectorAll(SLIDE));
    const navigation = carousel.querySelector('[data-bl-v9-focus-navigation]');
    const previous = carousel.querySelector('[data-bl-v9-focus-prev]');
    const next = carousel.querySelector('[data-bl-v9-focus-next]');
    const dots = Array.from(carousel.querySelectorAll('[data-bl-v9-focus-dot]'));
    if (!viewport || !track || !slides.length) return;
    carousel.dataset.blV9FocusReady = 'true';
    carousel.classList.add('is-static');
    carousel.classList.remove('is-running', 'is-paused');
    if (navigation) {
      navigation.hidden = slides.length < 2;
      navigation.removeAttribute('aria-hidden');
    }
    track.style.removeProperty('transform');
    slides.forEach((slide) => slide.setAttribute('aria-hidden', 'false'));
    let frame = 0;

    const maximum = () => Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const positions = () => {
      const left = slides[0].getBoundingClientRect().left;
      return slides.map((slide) => slide.getBoundingClientRect().left - left);
    };
    const currentIndex = () => {
      const x = viewport.scrollLeft;
      if (x >= maximum() - 2 && maximum() > 0) return slides.length - 1;
      const values = positions();
      return values.reduce((best, value, index) =>
        Math.abs(value - x) < Math.abs(values[best] - x) ? index : best, 0);
    };
    const sync = () => {
      frame = 0;
      const index = currentIndex();
      const x = viewport.scrollLeft;
      if (previous) previous.disabled = x <= 2;
      if (next) next.disabled = x >= maximum() - 2;
      slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
      carousel.dataset.currentSlide = String(index);
    };
    const queueSync = () => {
      if (!frame) frame = window.requestAnimationFrame(sync);
    };
    const select = (index) => {
      const target = positions()[Math.max(0, Math.min(slides.length - 1, index))];
      viewport.scrollTo({
        left: Math.max(0, Math.min(maximum(), target)),
        behavior: reducedMotion.matches ? 'instant' : 'smooth'
      });
    };
    const move = (direction) => {
      const x = viewport.scrollLeft;
      const values = positions();
      const target = direction > 0
        ? values.find((value) => value > x + 3) ?? maximum()
        : values.slice().reverse().find((value) => value < x - 3) ?? 0;
      viewport.scrollTo({
        left: Math.max(0, Math.min(maximum(), target)),
        behavior: reducedMotion.matches ? 'instant' : 'smooth'
      });
    };
    previous?.addEventListener('click', () => move(-1));
    next?.addEventListener('click', () => move(1));
    dots.forEach((dot, index) => dot.addEventListener('click', () => select(index)));
    viewport.addEventListener('scroll', queueSync, { passive: true });
    viewport.addEventListener('keydown', (event) => {
      if (event.target !== viewport) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        move(event.key === 'ArrowRight' ? 1 : -1);
      } else if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        select(event.key === 'Home' ? 0 : slides.length - 1);
      }
    });
    const observer = 'ResizeObserver' in window ? new ResizeObserver(queueSync) : null;
    observer?.observe(viewport);
    slides.forEach((slide) => observer?.observe(slide));
    window.addEventListener('resize', queueSync, { passive: true });
    carousel.blV9SelectFocusSlide = (slideOrIndex) => {
      const index = typeof slideOrIndex === 'number' ? slideOrIndex : slides.indexOf(slideOrIndex);
      if (index >= 0) select(index);
    };
    const cleanup = (event) => {
      if (!event.target.contains(carousel)) return;
      observer?.disconnect();
      window.removeEventListener('resize', queueSync);
      if (frame) window.cancelAnimationFrame(frame);
      document.removeEventListener('shopify:section:unload', cleanup);
    };
    document.addEventListener('shopify:section:unload', cleanup);
    sync();
  };
  const boot = (scope = document) => {
    if (scope.matches?.(CAROUSEL)) initialize(scope);
    scope.querySelectorAll?.(CAROUSEL).forEach(initialize);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest?.(SLIDE);
    slide?.closest(CAROUSEL)?.blV9SelectFocusSlide?.(slide);
  });
})();
